import axios from 'axios';
import * as messageAlert from '../actions/messageAlertAction';
import { I18n } from 'react-redux-i18n-lite';
import { API_LANGUAGE_PREFIX, DRAWER_CONTENTS, ERROR, NETWORK_ERROR, ROUTES } from '../const';
import routerHistory from '../routerHistory';
import * as bookActions from '../actions/bookActions';
import * as accountActions from '../actions/accountActions';
import * as connectionActions from '../actions/connectionActions';
import * as classroomActions from '../actions/classroom/classroomActions';
import * as remoteImageLoaderService from '../services/apiLoader/remoteImageLoaderService';
import * as taskActions from '../actions/taskActions';
import { getLogger } from '../services/logger';
import { setErrorData } from '../actions/errorActions';
import { getGuestTokenFromStorage, getRefreshTokenFromStorage, getTokenFromStorage } from '../shared';
import * as jwtService from '../services/jwt/jwtService';
import * as guestActions from '../actions/guestActions';
import { isElectronApp } from '../services/crossPlatform/crossPlatformService';

const debug = getLogger('interceptors:responseService');

const requestTokenSubscribersCallback = [];
const isElectron = isElectronApp();
const CMI5_DOWNLOAD_URL_PATTERN = /.+\api\/v1\/classrooms\/.+\/books\/.+\/cmi5\?activityId=.+/m;

// Función para detectar si estamos en Moodle
const isMoodleEnvironment = () => {
  const currentUrl = window.location.href;
  return currentUrl.includes('lis_outcome_service_url') || 
         currentUrl.includes('lis_result_sourcedid') || 
         currentUrl.includes('lis_resource_id');
};

export const addRequestTokenSubscribersCallback = callback => {
  requestTokenSubscribersCallback.push(callback);
};

export const getRequestTokenSubscribersCallback = () => {
  return requestTokenSubscribersCallback.splice(0, requestTokenSubscribersCallback.length);
};

export const setResponseInterceptor = (store = {}) => {
  const manageNotAuthorizedStatus = (error, originalRequest) => {
    if (originalRequest.silentAuthError) {
      return Promise.reject(error);
    }

    const { dispatch } = store;
    const token = getTokenFromStorage();
    const refreshToken = getRefreshTokenFromStorage();
    const guestToken = getGuestTokenFromStorage();

    if (!token && guestToken) {
      // Sesión de invitado: no hay refresh token, no se intenta el flujo de refresco/logout de usuarios reales.
      dispatch(guestActions.setSessionExpiredModal(true));
      return Promise.reject(error);
    }

    if (token) {
      if (jwtService.hasExpired(refreshToken) || originalRequest.url.includes('refresh-token')) {
        dispatch(accountActions.logout());
      }

      dispatch(accountActions.forceCheckAndRefreshToken());

      return new Promise(resolve => {
        addRequestTokenSubscribersCallback(() => {
          delete originalRequest.headers.Authorization;
          resolve(axios(originalRequest));
        });
      });
    }

    dispatch(messageAlert.error(I18n.t('login.error')));
  };

  const defaultError = error => {
    const { dispatch } = store;
    let codeToTranslate = '';

    debug.error('[defaultError] %s', error);

    if (error.data && error.data.code) {
      codeToTranslate = `${API_LANGUAGE_PREFIX}${error.data.code}`;
      dispatch(messageAlert.error(I18n.t(codeToTranslate)));
    } else if (error.response.data && error.response.data.code) {
      codeToTranslate = `${API_LANGUAGE_PREFIX}${error.response.data.code}`;
      dispatch(messageAlert.error(I18n.t(codeToTranslate)));
    } else if (error.code) {
      codeToTranslate = `${API_LANGUAGE_PREFIX}${error.code}`;
      dispatch(messageAlert.error(I18n.t(codeToTranslate)));
    } else if (error.message) {
      dispatch(messageAlert.error(error.message));
    }
  };

  const manageNotFoundStatus = error => {
    if (error.request.responseURL.match(/classrooms.*components/)) {
      return;
    }
    // silentNotFound: el llamante gestiona el 404 (p.ej. tareas cuyo libro se ha borrado)
    if (error.config && error.config.silentNotFound) {
      return;
    }
    const { dispatch } = store;
    const codeError = error.response.data && error.response.data.code ? error.response.data.code : '';
    
    if (codeError === ERROR.CLASSROOM_ARCHIVED.code) {
      manageClassroomArchivedError(error);
    } else if (codeError === ERROR.NOT_FOUND_CLASSROOM.code) {
      dispatch(setErrorData());
      if (store.getState().account.jailed) {
        // Si es desde Moodle, no manejar aquí para permitir manejo en bookActions
        return;
        // routerHistory.push(ROUTES.ERROR_JAILED);
      } else {
        routerHistory.push(ROUTES.ERROR);
      }
    } else if (ERROR.NOT_FOUND_BOOK.code === codeError || ERROR.NOT_FOUND_ACTIVITY.code === codeError) {
      // Si es desde Moodle, no manejar aquí para permitir manejo en bookActions
      if (store.getState().account.jailed) {
        return;
      }
      
      const { drawer } = store.getState();
      if (
        drawer.open &&
        (DRAWER_CONTENTS.INSTRUCTOR_TASK === drawer.content ||
          DRAWER_CONTENTS.STUDENT_TASK === drawer.content ||
          DRAWER_CONTENTS.DETAIL_ASSIGNMENTS === drawer.content)
      ) {
        debug.error('[SilentError] %s', error);
        dispatch(taskActions.setTaskContentsNotFound(true));
      } else {
        defaultError(error);
      }
    } else if (codeError !== ERROR.NOT_FOUND_GOOGLE.code) {
      defaultError(error);
    }
  };

  const manageForbiddenError = error => {
    const { dispatch } = store;
    const token = getTokenFromStorage();
    const guestToken = getGuestTokenFromStorage();
    const codeError = error.response.data && error.response.data.code ? error.response.data.code : '';
    if (!token && guestToken) {
      dispatch(messageAlert.error(I18n.t(ERROR.USER_FORBIDDEN.translationCode)));
      return;
    }
    if (codeError === ERROR.CLASSROOM_FORBIDDEN.code) {
      dispatch(setErrorData('403', ERROR.CLASSROOM_FORBIDDEN.translationCode));
      debug.error('[manageForbiddenError]', error);
      if (store.getState().account.jailed) {
        routerHistory.push(ROUTES.ERROR_JAILED);
      } else {
        routerHistory.push(ROUTES.ERROR);
      }
      // TODO: quitar cuando el MIS devuelva siempre 401
    } else if (codeError === ERROR.USER_FORBIDDEN.code) {
      dispatch(messageAlert.error(I18n.t(ERROR.USER_FORBIDDEN.translationCode)));
    } else if (!token) {
      dispatch(messageAlert.error(I18n.t('login.error')));
    } else {
      defaultError(error);
    }
  };

  const manageClassroomArchivedError = error => {
    const { dispatch } = store;
    debug.error('[manageClassroomArchivedError]', error);

    routerHistory.push(ROUTES.HOME);
    setTimeout(() => dispatch(messageAlert.error(I18n.t('classroom.archived'))), 500);
  };

  const manageConflictStatus = error => {
    const { dispatch } = store;
    debug.error('[manageConflictStatus]', error);

    if (error.response.data && error.response.data.code) {
      const codeToTranslate = `${API_LANGUAGE_PREFIX}${error.response.data.code}`;
      dispatch(messageAlert.error(I18n.t(codeToTranslate)));
    }
  };

  const manageNetworkError = error => {
    // SMARTCLASS-3515 con la refactorizacion de la parte del contenido no deberia haber estos errores
    if (!isCmi5DownloadError(error)) {
      const { dispatch } = store;

      if (routerHistory.getCurrentLocation().pathname.indexOf('login/auth') === -1) {
        dispatch(connectionActions.forceTryToConnect());
      } else {
        dispatch(messageAlert.error(error.message));
      }
    }
  };

  const manageBadRequestStatus = error => {
    const { dispatch } = store;

    if (error.response.data && error.response.data.code === ERROR.WRONG_SCHOOL_CODE.code) {
      const codeToTranslate = `${API_LANGUAGE_PREFIX}${error.response.data.code}`;
      dispatch(messageAlert.error(I18n.t(codeToTranslate)));
    }
  };

  const manageInternalErrorStatus = error => {
    const { dispatch } = store;
    debug.error('[manageInternalErrorStatus]', I18n.t(ERROR.INTERNAL_ERROR.translationCode));
    const codeToTranslate = `${API_LANGUAGE_PREFIX}error.${error.response.status}`;
    dispatch(messageAlert.error(I18n.t(codeToTranslate)));
  };

  const manageRequestEntityTooLarge = error => {
    const { dispatch } = store;
    debug.error('[manageRequestEntityTooLarge]', error);
    const codeToTranslate = `${API_LANGUAGE_PREFIX}error.${error.response.status}`;
    dispatch(messageAlert.error(I18n.t(codeToTranslate)));
  };

  const manageRequestLimitUploads = error => {
    const { dispatch } = store;
    dispatch(classroomActions.setNotCanUploadFilesModalIsOpen(true));
  };

  const manageAcceptedStatus = () => {
    const { dispatch } = store;
    dispatch(messageAlert.info(I18n.t('alert.preparingBook')));
  };

  const manageRemoteImages = responseData => {
    const { getState } = store;
    const state = getState();

    if (state && state.connection.isLoadingData) {
      const remoteImages = remoteImageLoaderService.getImagesFromObject(responseData);
      remoteImageLoaderService.fetchRemoteImages(remoteImages);
    }
  };

  const manageCancelRequest = () => {
    const { dispatch } = store;
    dispatch(bookActions.setUploadContentStatus());
  };

  const handleGatewayTimeout = error => {
    // SMARTCLASS-3515 con la refactorizacion de la parte del contenido no deberia haber estos errores
    if (!isCmi5DownloadError(error)) {
      defaultError(error);
    }
  };

  const isCmi5DownloadError = error => {
    return isElectron && error.config && isCmi5DownloadUrl(error.config.url);
  };

  const isCmi5DownloadUrl = url => {
    return CMI5_DOWNLOAD_URL_PATTERN.test(url);
  };

  axios.interceptors.response.use(
    function(response) {
      manageRemoteImages(response.data);

      if (response.status === 202) {
        manageAcceptedStatus();
      }

      return response;
    },
    async function(error) {
      if (error.message === NETWORK_ERROR) {
        manageNetworkError(error);
      } else if (error.response) {
        const {
          config,
          response: { status }
        } = error;
        if (status === 400) {
          manageBadRequestStatus(error);
        } else if (status === 401) {
          return await manageNotAuthorizedStatus(error, config);
        } else if (status === 403) {
          manageForbiddenError(error);
        } else if (status === 404) {
          manageNotFoundStatus(error);
        } else if (status === 409) {
          manageConflictStatus(error);
        } else if (status === 500) {
          manageInternalErrorStatus(error);
        } else if (status === 504) {
          // SMARTCLASS-3515 con la refactorizacion de la parte del contenido no deberia haber estos errores
          handleGatewayTimeout(error);
        } else if (status === 413) {
          manageRequestEntityTooLarge(error);
        } else if (status === 429) {
          manageRequestLimitUploads(error);
        } else {
          defaultError(error);
        }
      } else if (axios.isCancel(error)) {
        manageCancelRequest();
      } else {
        defaultError(error);
      }
      return Promise.reject(error);
    }
  );
};
